import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  Image,
  JustifyContent,
  Space,
  Text,
  useTheme,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React from 'react'

import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../../../tracking'

const SUPPLIERS_URL = 'https://www.gousto.co.uk/blog/suppliers'

export type CWCSupplierDetails = {
  title: string
  location: string
  since: string
  /** Certification tag; omitted for suppliers without one (Cidacos, Dunbia). */
  tag?: string
}

export type CWCSupplierCardProps = {
  webpSmall: string
  webpLarge: string
  fallback: string
  alt: string
  supplierDetails: CWCSupplierDetails
}

// Anchors do NOT activate on Space (Space scrolls the page) — add it for button-consistent
// keyboard operability, mirroring CWCLinkButton's handleKeyDown.
const handleCardKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
  if (event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault()
    event.currentTarget.click()
  }
}

// The whole card is a real anchor to the suppliers page. 16px radius, clipped image, and the
// hover/focus states the AC requires: a lift on pointer hover and a visible focus-visible outline.
const CardLink = withCitrusTheme('a', (theme) => ({
  display: 'block',
  position: 'relative',
  textDecoration: 'none',
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'transform 150ms ease',
  ':hover': {
    transform: 'translateY(-2px)',
  },
  ':focus-visible': {
    outline: `2px solid ${theme.color(Color.Secondary_400)}`,
    outlineOffset: '2px',
  },
}))

// Bottom gradient so the overlaid text stays legible over any supplier photo:
// transparent from ~27.5% down to opaque #111827 (Figma).
const GradientOverlay = withCitrusTheme(Box, () => ({
  background: 'linear-gradient(180deg, rgba(17, 24, 39, 0) 27.5%, #111827 100%)',
}))

// Overlay type scale (constant across breakpoints per designer note): title 16/20 bold white,
// location/since 14/20, certification tag 12/16. `since`/tag use bespoke greys with no Citrus
// Color token, so they are raw hex. Sizes go through full-length responsiveStyle arrays (one
// value per breakpoint) — a single-value array or a plain fontSize loses to Citrus `Text`'s own
// @media default at ≥768.
const Title = withCitrusTheme(Text, (theme) => [
  { color: theme.color(Color.White), fontFamily: theme.tokens.FontFamilies.bold },
  theme.responsiveStyle(['16px', '16px', '16px'], ['fontSize']),
  theme.responsiveStyle(['20px', '20px', '20px'], ['lineHeight']),
])

const Location = withCitrusTheme(Text, (theme) => [
  { color: theme.color(Color.White) },
  theme.responsiveStyle(['14px', '14px', '14px'], ['fontSize']),
  theme.responsiveStyle(['20px', '20px', '20px'], ['lineHeight']),
])

const Since = withCitrusTheme(Text, (theme) => [
  { color: '#CED4D9' },
  theme.responsiveStyle(['14px', '14px', '14px'], ['fontSize']),
  theme.responsiveStyle(['20px', '20px', '20px'], ['lineHeight']),
])

const Tag = withCitrusTheme(Text, (theme) => [
  { color: '#ECEDEF' },
  theme.responsiveStyle(['12px', '12px', '12px'], ['fontSize']),
  theme.responsiveStyle(['16px', '16px', '16px'], ['lineHeight']),
])

export function CWCSupplierCard({
  webpSmall,
  webpLarge,
  fallback,
  alt,
  supplierDetails,
}: CWCSupplierCardProps) {
  const { theme } = useTheme()

  const trackClick = () => {
    trackCTAClick(
      TrackingActions.OUR_SUPPLIERS_CAROUSEL,
      LandingPageContentSections.OUR_SUPPLIERS,
      {
        supplierDetails: supplierDetails.title,
      },
    )
  }

  return (
    <CardLink href={SUPPLIERS_URL} onClick={trackClick} onKeyDown={handleCardKeyDown}>
      <Box
        // Fixed (not max-) so the card holds its size before the lazy-loaded image arrives —
        // otherwise, with only maxWidth/maxHeight and no other in-flow content, the card collapses
        // until the image loads, then jumps by ~320-360px, shifting the CTA and sections below.
        height={['320px', null, '360px']}
        width={['220px', null, '230px']}
        position={DisplayPosition.Relative}
      >
        <picture>
          <source
            srcSet={webpSmall}
            media={`(max-width: ${theme.tokens.breakpoints[1]})`}
            type="image/webp"
          />
          <source
            srcSet={webpLarge}
            media={`(min-width: ${theme.tokens.breakpoints[1]})`}
            type="image/webp"
          />
          <Image
            src={fallback}
            alt={alt}
            width="100%"
            height="100%"
            style={{ objectFit: 'cover' }}
            loading="lazy"
          />
        </picture>
        <GradientOverlay position={DisplayPosition.Absolute} bottom="0" left="0" right="0">
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.FlexStart}
            justifyContent={JustifyContent.FlexStart}
            paddingH={4}
            paddingV={3}
          >
            <Title>{supplierDetails.title}</Title>
            <Location>{supplierDetails.location}</Location>
            <Since>{supplierDetails.since}</Since>
            <Space size={2} />
            {/* Reserves the tag row's height even without one (Cidacos, Dunbia), matching the
              Control SupplierCard's minHeight wrapper — otherwise these two cards' titles sit at
              a different vertical position than the tagged cards. */}
            <Box minHeight="16px">
              {supplierDetails.tag ? <Tag>{supplierDetails.tag}</Tag> : null}
            </Box>
          </Box>
        </GradientOverlay>
      </Box>
    </CardLink>
  )
}
